import { Splide, SplideSlide } from "@splidejs/react-splide";
import '@splidejs/splide/css';
import Styles from "./styles.module.css";
import React, { useEffect, useRef, useState } from "react";
import { IoImageOutline, IoChevronForward, IoClose } from "react-icons/io5";
import Pager from "./../pager/index";
import DefaultImg from "./../../../../image/default/recruit.png";
import { interviewsData } from "src/type";

// 画像未登録・読み込み失敗時にブラウザの「壊れた画像」表示になるのを防ぎ、
// NO IMAGE のプレースホルダー画像に差し替える。
const handleImgError = (e: React.SyntheticEvent<HTMLImageElement>) => {
  e.currentTarget.onerror = null;
  e.currentTarget.src = DefaultImg;
};

// 求人編集プレビューで未入力欄に出す例文（灰色）。公開ページでは使われない。
const EXAMPLE = {
  name: "例）山田 花子",
  title: "例）スタイリスト／入社4年目",
  desc: "例）教育制度やイベントなど、成長できる環境が伝わる写真と説明にしましょう。",
};

type InterviewItem = { name: string; title: string; desc: string; image: string };

type RecruitPagesInterviewsProps = {
  interviews: interviewsData;
  // 求人編集のプレビュー時のみ true。未登録の写真・未入力欄に例（灰色）を出す。
  // 公開求人ページからは渡されないため、応募者には従来どおり何も出ない。
  preview?: boolean;
};

const RecruitPagesInterviews: React.FC<RecruitPagesInterviewsProps> = React.memo(({ interviews, preview }) => {

  const [ time, setTime ] = useState<number>(0);

  useEffect(() => {
    var timeInit = new Date().getTime();
    setTime(timeInit);
  }, []);

  const carouselRef = useRef<any>(null);
  const [ currentIndex, setCurrentIndex ] = useState(0);
  const [ opened, setOpened ] = useState<InterviewItem | null>(null);

  // 公開ページは、名前も文章も無い枠を出さない(学生アプリと同じ)。編集プレビューは枠をそのまま出す
  const list: InterviewItem[] = [
    interviews.item1, interviews.item2, interviews.item3,
    interviews.item4, interviews.item5, interviews.item6,
  ].slice(0, interviews.count)
    .filter((e) => preview || (e?.name?.trim() || e?.desc?.trim()));

  const imageUrl = (image: string, size: string) =>
    `https://firebasestorage.googleapis.com/v0/b/trankllc-com.appspot.com/o/interview%2Fresize_images%2F${image}_${size}?alt=media&token=${time}`;

  const renderImg = (image: string) => {
    if (preview && !image) {
      return (
        <div className={Styles.noImage}>
          <IoImageOutline className={Styles.noImageIcon} />
          <span>写真を追加するとここに表示されます</span>
        </div>
      );
    }
    return (
      <img className={Styles.slideImg}
        src={imageUrl(image, "1080x1080")}
        alt="美容室 求人 memorii works" onError={handleImgError} />
    );
  };

  const cls = (base: string, empty: boolean) =>
    preview && empty ? `${base} ${Styles.previewPh}` : base;

  // 1人分のカード(写真・名前・肩書き・コメントの冒頭。押すと全文)
  const renderSlide = (item: InterviewItem) => (
    <div className={Styles.interview} onClick={() => item.desc?.trim() && setOpened(item)}>
      {renderImg(item.image)}
      <div className={Styles.card_body}>
        <h4 className={cls(Styles.text_h4, !item.name)}>
          {item.name || (preview ? EXAMPLE.name : "")}
        </h4>
        {(item.title || preview) && (
          <h6 className={cls(Styles.text_h6, !item.title)}>
            {item.title || EXAMPLE.title}
          </h6>
        )}
        {(item.desc?.trim() || preview) && (
          <>
            <p className={cls(Styles.text_p, !item.desc)}>
              {item.desc ? `\u201c${item.desc.replace(/\n/g, " ").trim()}\u201d` : EXAMPLE.desc}
            </p>
            {item.desc?.trim() && (
              <span className={Styles.more}>続きを読む<IoChevronForward /></span>
            )}
          </>
        )}
      </div>
    </div>
  );

  const pages = Math.ceil(list.length / 2);
  const page = Math.floor(currentIndex / 2);

  return (
    <>
    {pages > 1 && (
      <Pager
        index={page}
        total={pages}
        unit="ページ"
        onPrev={() => carouselRef.current?.splide.go(Math.max(0, (page - 1) * 2))}
        onNext={() => carouselRef.current?.splide.go(Math.min(list.length - 1, (page + 1) * 2))} />
    )}

    <div className={Styles.block}>
      <Splide
        ref={carouselRef}
        aria-label=""
        options={{
          perPage: 2,
          arrows: false,
          pagination: false,
          gap: 10,
        }}
        onMove={(splide, newIndex) => {
          setCurrentIndex(newIndex);
        }}
      >
        {list.map((item, i) => <SplideSlide key={i}>{renderSlide(item)}</SplideSlide>)}
      </Splide>
    </div>

    {opened && (
      <div className={Styles.modal} role="dialog" aria-modal="true" onClick={() => setOpened(null)}>
        <div className={Styles.modal_box} onClick={(e) => e.stopPropagation()}>
          <div className={Styles.modal_head}>
            <img className={Styles.modal_avatar} src={imageUrl(opened.image, "500x500")} alt="" onError={handleImgError} />
            <div className={Styles.modal_name}>
              <b>{opened.name}</b>
              {opened.title?.trim() && <span>{opened.title}</span>}
            </div>
            <button type="button" className={Styles.modal_close} aria-label="閉じる" onClick={() => setOpened(null)}>
              <IoClose />
            </button>
          </div>
          <div className={Styles.modal_text}>{opened.desc}</div>
        </div>
      </div>
    )}
    </>
  );
});

export default RecruitPagesInterviews;
